import { X } from 'lucide-react';
import { useIntl } from 'react-intl';

import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyH5 } from '@heidi/design-system/typography';

import type {
  PermissionMode,
  PermissionName,
  PermissionStatusMap,
} from '@heidi/agents/contracts';
import {
  desktopPermissionGateDescription,
  desktopPermissionGateTitle,
} from './desktop-permission-gate-copy';
import { PermissionRowList } from './permission-row-list';

interface DesktopPermissionPanelProps {
  mode: PermissionMode;
  missing: readonly PermissionName[];
  statuses: PermissionStatusMap;
  onDismiss?: () => void;
  onRequestPermission: (permission: PermissionName) => void;
}

export function DesktopPermissionPanel({
  mode,
  missing,
  statuses,
  onDismiss,
  onRequestPermission,
}: DesktopPermissionPanelProps) {
  const intl = useIntl();

  return (
    <div
      className={cn(
        'border border-border bg-surface-elevated shadow-sheet',
        'relative flex w-full max-w-md flex-col gap-5 rounded-xl p-5'
      )}
    >
      {onDismiss ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="absolute end-3 top-3 text-text-secondary"
          onClick={onDismiss}
          aria-label={intl.formatMessage({
            defaultMessage: 'Close',
            id: '3SxSYgWd9G',
            description: 'Dismiss the desktop permission gate card',
          })}
        >
          <X className="size-4" />
        </Button>
      ) : null}
      <div className="flex flex-col gap-1 pe-6">
        <TypographyH5>{desktopPermissionGateTitle(intl, mode)}</TypographyH5>
        <TypographyCaption className="text-text-secondary">
          {desktopPermissionGateDescription(intl, mode)}
        </TypographyCaption>
      </div>
      <PermissionRowList
        rows={missing}
        statuses={statuses}
        onRequestPermission={onRequestPermission}
      />
    </div>
  );
}
